/**
 * Implement a class that tracks a thread of messages between the user and the assistant.
 * - constructor({messages: Message[], tools: Tool[], driver: BedrockThreadDriver}) - initialize the thread with a list of messages and tools
 * - push() - add a message to the thread
 * - shift() - remove the oldest message from the thread
 * - pop() - remove the newest message from the thread
 * - last() - get the oldest message from the thread
 * - size() - get the number of messages in the thread
 * - clear(filter?: (message: Message) => boolean) - remove all messages from the thread
 * - all() - get all messages from the thread
 * - handle(message: Message) - handle a message from the LLM
 *   - includes tool calling or triggering events
 * - send(message?: Message) - send a message or the current thread state to the LLM
 * - fork() - create a new thread with the same history and tools
 */

import { randomUUID } from "node:crypto";
import { EventSource } from "./event-source";
import type { Message, ToolCall } from "./message";
import { Tool, type ToolInterface, makeToolFromInterface } from "./tool";

export interface ThreadDriver {
  sendThread(thread: Thread): Promise<Thread>;
  streamThread(thread: Thread): AsyncGenerator<{ stream: AsyncGenerator<string, void>, message: Message }, Thread>;
  parallelToolCalls?: boolean;
}

export class Thread extends EventSource {
  private messages: Message[];
  private readonly tools: Tool[];
  public readonly toolChoice: "auto" | "any";
  private readonly driver: ThreadDriver;
  private readonly maxSteps: number;
  private currentSteps: number;
  private responseHandlers: ((message: Message) => Promise<void>)[];
  public readonly id: string;
  public readonly parallelToolCalls: boolean;

  constructor({
    messages = [],
    tools = [],
    driver,
    toolChoice = "auto",
    maxSteps = 30,
    parallelToolCalls = false,
  }: {
    messages?: Message[];
    tools?: ToolInterface[];
    toolChoice?: "auto" | "any";
    driver: ThreadDriver;
    maxSteps?: number;
    parallelToolCalls?: boolean;
  }) {
    super();
    this.messages = messages;
    this.tools = tools.map(makeToolFromInterface);
    this.driver = driver;
    this.maxSteps = maxSteps;
    this.currentSteps = 0;
    this.responseHandlers = [];
    this.toolChoice = toolChoice;
    this.parallelToolCalls = parallelToolCalls;
    this.id = this.generateThreadId();
    // Forward events from all tools with thread context
    tools.forEach((tool) => this.forwardEvents(tool, { thread: this }));

    // Validate thread on construction
    this.validateThread();
  }

  /**
   * Register a handler to be called when an assistant message without tool calls is received
   */
  public onResponse(handler: (message: Message) => Promise<void>): void {
    this.responseHandlers.push(handler);
  }

  /**
   * Execute all registered response handlers
   */
  private async executeResponseHandlers(message: Message): Promise<void> {
    for (const handler of this.responseHandlers) {
      await handler(message);
    }
  }

  /**
   * Add a message to the thread
   */
  public push(message: Message): void {
    this.messages.push(message);
    this.emit("thread.message-added", { thread: this, message });
  }

  /**
   * Remove the most recent message from the thread
   */
  public pop(): Message | undefined {
    const message = this.messages.pop();
    if (message) {
      this.emit("thread.message-removed", { thread: this, message });
    }
    return message;
  }

  /**
   * Remove the oldest message from the thread
   */
  public shift(): Message | undefined {
    const message = this.messages.shift();
    if (message) {
      this.emit("thread.message-removed", { thread: this, message });
    }
    return message;
  }

  /**
   * Get the most recent message from the thread
   */
  public last(): Message | undefined {
    return this.messages[this.messages.length - 1];
  }

  /**
   * Get the number of messages in the thread
   */
  public size(): number {
    return this.messages.length;
  }

  /**
   * Remove all messages from the thread that match the filter
   * If no filter is provided, remove all messages
   */
  public clear(filter?: (message: Message) => boolean): void {
    const removedMessages = filter
      ? this.messages.filter(filter)
      : [...this.messages];
    if (filter) {
      this.messages = this.messages.filter((message) => !filter(message));
    } else {
      this.messages = [];
    }
    this.emit("thread.messages-cleared", { thread: this, removedMessages });
  }

  /**
   * Get all messages from the thread
   */
  public all(): Message[] {
    return [...this.messages];
  }

  /**
   * Check if the thread has reached its maximum number of steps
   * @returns true if max steps reached, false otherwise
   */
  private checkMaxSteps(): boolean {
    this.currentSteps++;
    if (this.currentSteps >= this.maxSteps) {
      this.debug("MAX STEPS REACHED");
      this.emit("thread.max_steps_reached", {
        thread: this,
        steps: this.currentSteps,
      });
      return true;
    }
    return false;
  }

  /**
   * Execute a tool call and process its response
   * @param toolCall The tool call to execute
   * @param tool The tool to execute with
   */
  private async executeToolCall(toolCall: ToolCall, tool: Tool): Promise<void> {
    this.debug(`[DEBUG] Executing tool call: ${tool.getName()}, args:`, JSON.stringify(toolCall.arguments, null, 2));
    const result = await tool.executeTool(toolCall.arguments, toolCall);
    this.debug(`[DEBUG] Tool execution result:`, JSON.stringify(result, null, 2));
    const toolMessages = tool.toMessages(result);
    toolMessages.forEach((message) => this.push(message));
    await this.send(undefined, true);
  }

  /**
   * Process a message containing tool calls
   * @param message The message containing tool calls
   */
  private async processToolCalls(message: Message): Promise<void> {
    const tools = this.getTools();
    if (this.parallelToolCalls) {
      // Run all tool calls in parallel
      const toolCallPromises = message.toolCalls.map((toolCall) => {
        const tool = tools.find((t) => t.getName() === toolCall.name);
        if (tool) {
          return this.executeToolCall(toolCall, tool);
        }
        return Promise.resolve(); // Add explicit return for when no tool is found
      });
      await Promise.all(toolCallPromises);
    } else {

      for (const toolCall of message.toolCalls) {
        const tool = tools.find((t) => t.getName() === toolCall.name);
        if (tool) {
          if (this.checkMaxSteps()) {
            return;
          }
          await this.executeToolCall(toolCall, tool);
        }
      }
    }
  }

  /**
   * Process an assistant message without tool calls
   * @param message The assistant message to process
   */
  private async processAssistantMessage(message: Message): Promise<void> {
    await this.executeResponseHandlers(message);
    this.emit("thread.response", { thread: this, message });
  }

  /**
   * Handle a message from the LLM
   * This includes processing tool calls and triggering events
   */
  public async processLastMessage(): Promise<Thread> {
    const lastMessage = this.last();
    if (!lastMessage) return this;

    this.debug("HANDLING RESPONSE", lastMessage);
    if (lastMessage.isToolCall()) {
      await this.processToolCalls(lastMessage);
    } else if (lastMessage.isAssistantMessage()) {
      await this.processAssistantMessage(lastMessage);
    }

    return this;
  }

  /**
   * Send a message or the current thread state to the LLM
   */
  public async send(message?: Message, toolResponse = false): Promise<this> {
    if (message) {
      this.push(message);
    }
    // Only reset steps on explicit message sends, not tool-triggered sends
    if (!toolResponse) {
      this.currentSteps = 0;
    }

    this.validateThread();

    this.debug("SENDING MESSAGE", this.last());
    await this.driver.sendThread(this);
    this.emit("thread.sent", { thread: this, message });
    await this.processLastMessage();
    return this;
  }

  /**
   * Wraps a stream with completion handling
   */
  private async *wrapStream(stream: AsyncGenerator<string, void>, onComplete: () => Promise<void>) {
    try {
      yield* stream;
    } finally {
      await onComplete();
    }
  }

  /**
   * Stream a message or the current thread state to the LLM
   */
  public async stream(message?: Message, toolResponse = false): Promise<AsyncGenerator<string, void>> {
    if (message) {
      this.push(message);
    }
    // Only reset steps on explicit message sends, not tool-triggered sends
    if (!toolResponse) {
      this.currentSteps = 0;
    }

    this.validateThread();

    this.debug("STREAMING MESSAGE", this.last());
    
    const response = this.driver.streamThread(this);
    const firstYield = await response.next();
    
    if (!firstYield.value || !('stream' in firstYield.value) || !('message' in firstYield.value)) {
      throw new Error("Invalid response received from driver");
    }

    const { stream, message: initialMessage } = firstYield.value;

    // Remove any existing assistant message
    if (this.last()?.role === "assistant") {
      this.pop();
    }
    this.push(initialMessage);

    return this.wrapStream(stream, () => this.streamComplete(response));
  }

  /**
   * Handle the completion of a stream
   */
  private async streamComplete(streamResponse: AsyncGenerator<{ stream: AsyncGenerator<string, void>, message: Message }, Thread>) {
    try {
      const result = await streamResponse.next();
      if (result.value && result.value instanceof Thread) {
        this.emit("thread.sent", { thread: this, message: this.last() });
        await this.processLastMessage();
      }
    } catch (error) {
      this.emit("thread.error", { thread: this, error });
    }
  }

  /**
   * Validates that the thread has a user message as the first non-system message
   * @throws Error if validation fails
   */
  private validateThread(): void {
    const nonSystemMessages = this.messages.filter(
      (message) => !message.isSystemMessage()
    );

    if (nonSystemMessages.length === 0) {
      throw new Error("Thread must contain at least one non-system message");
    }

    const firstNonSystemMessage = nonSystemMessages[0];

    if (!firstNonSystemMessage?.isUserMessage()) {
      throw new Error(
        "First non-system message in thread must be a user message"
      );
    }
  }

  /**
   * Get all available tools in the thread
   */
  public getTools(): Tool[] {
    return [...this.tools];
  }
  
  /**
   * Add a new tool to the thread
   * @param tool The tool to add
   * @returns The tool that was added
   */
  public addTool(tool: Tool | ToolInterface): Tool {
    const newTool = tool instanceof Tool ? tool : makeToolFromInterface(tool);
    this.tools.push(newTool);
    this.forwardEvents(newTool, { thread: this });
    this.emit("thread.tool-added", { thread: this, tool: newTool });
    return newTool;
  }

  /**
   * Generate a unique thread ID using UUID v4
   */
  private generateThreadId(): string {
    return randomUUID();
  }

  /**
   * Create a new thread with the same message history and tools
   */
  public fork(): Thread {
    return new Thread({
      messages: [...this.messages],
      tools: [...this.tools],
      driver: this.driver,
    });
  }
}
